// EstimateSummaryDrawer — fixed-bottom mobile drawer. The peek bar shows the
// current estimate range and a "Tap for details" affordance; tapping expands
// the area/population breakdown. The Send Inquiry button stays visible (outside
// the collapse) whenever a product is selected, so it's reachable without
// swiping up.
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Paper, Collapse, ButtonBase, Button, Typography, Divider,
} from '@mui/material';
import { materialSymbolSx } from '../../theme.js';
import { estimateRangeText } from '../../utils/estimate-format.js';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

const StatRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="caption" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Box>
);

const EstimateSummaryDrawer = ({ onSendInquiry }) => {
  const [open, setOpen] = useState(false);
  const area = useSelector((s) => s.area.stats.data);
  const estimate = useSelector((s) => s.selections.estimate);
  const selectedCount = useSelector((s) => s.selections.items.length);
  const hasArea = !!area;
  const canSubmit = hasArea && selectedCount > 0;

  const toggle = useCallback(() => setOpen((v) => !v), []);

  const peekText = () => {
    if (!hasArea) return 'Choose a project area';
    if (selectedCount === 0) return 'Select products to estimate';
    if (estimate.loading) return 'Calculating…';
    if (estimate.data) {
      return estimateRangeText(estimate.data);
    }
    return 'Preliminary estimate';
  };

  return (
    <Paper
      role="complementary"
      aria-label="Estimate summary"
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 'drawer',
        borderRadius: '12px 12px 0 0',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: 6,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 36,
          height: 4,
          borderRadius: 2,
          bgcolor: 'divider',
        }}
      />
      <ButtonBase
        onClick={toggle}
        aria-expanded={open}
        aria-controls="esd-body"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          px: 2,
          pt: 1.5,
          pb: 1,
          textAlign: 'left',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Est. investment
          </Typography>
          {/* aria-live so screen readers catch estimate updates even when collapsed */}
          <Typography
            aria-live="polite"
            aria-atomic="true"
            sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'primary.main' }}
          >
            {peekText()}
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.25, color: 'text.secondary',
        }}
        >
          <Typography variant="caption">{open ? 'Hide details' : 'Tap for details'}</Typography>
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              ...materialSymbolSx,
              fontSize: 20,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          >
            expand_less
          </Box>
        </Box>
      </ButtonBase>

      <Collapse in={open}>
        <Box
          id="esd-body"
          sx={{
            px: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '50vh', overflowY: 'auto',
          }}
        >
          {hasArea ? (
            <>
              <StatRow label="Area" value={`${fmt(area.areaSqMi)} sq mi`} />
              <StatRow label="Est. population" value={fmt(area.estPopulation)} />
              <StatRow label="Products selected" value={selectedCount} />
              <Divider />
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Preliminary estimate — final pricing provided in formal proposal.
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Choose a project area to see your estimate.
            </Typography>
          )}
        </Box>
      </Collapse>

      {/* CTA stays out of the collapse so it's reachable without swiping up. */}
      {canSubmit && (
        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
          <Button variant="contained" fullWidth onClick={onSendInquiry}>
            Send Inquiry
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default EstimateSummaryDrawer;
